/**
 * Embeddings navigateur (worker) pour auto-complétion sémantique locale.
 */

let worker;
let jobId = 0;
const pending = new Map();

function getWorker() {
  if (!worker) {
    worker = new Worker(new URL('../workers/embedding.worker.js', import.meta.url));
    worker.onmessage = (e) => {
      const data = e.data || {};
      const { id, vec, buffers, error, count, type } = data;
      if (type === 'progress') {
        const p = pending.get(id);
        if (p && typeof p.onProgress === 'function') {
          p.onProgress({
            status: data.status,
            file: data.file,
            loaded: data.loaded,
            total: data.total,
            progress: data.progress,
          });
        }
        return;
      }
      if (type === 'ready') {
        const p = pending.get(id);
        if (p) {
          pending.delete(id);
          p.resolve(true);
        }
        return;
      }
      const p = pending.get(id);
      if (!p) return;
      pending.delete(id);
      if (error) {
        p.reject(new Error(error));
        return;
      }
      if (buffers && Array.isArray(buffers)) {
        const arrs = buffers.map((b) => new Float32Array(b));
        p.resolve(arrs);
        return;
      }
      if (vec) {
        p.resolve(new Float32Array(vec));
        return;
      }
      if (count === 0) {
        p.resolve([]);
      }
    };
    worker.onerror = (err) => {
      pending.forEach(({ reject }) => reject(err));
      pending.clear();
    };
  }
  return worker;
}

function runJob(payload, onProgress) {
  return new Promise((resolve, reject) => {
    const id = ++jobId;
    pending.set(id, { resolve, reject, onProgress });
    getWorker().postMessage({ ...payload, id });
  });
}

/**
 * Précharge le modèle dans le cache navigateur (IndexedDB, premier lancement coûteux).
 * @param {(info: { status?: string, file?: string, loaded?: number, total?: number, progress?: number }) => void} [onProgress]
 */
export function initEdgeEmbeddings(onProgress) {
  return runJob({ type: 'init' }, onProgress);
}

/**
 * @param {string} text
 * @returns {Promise<Float32Array>}
 */
export function embedQueryInBrowser(text) {
  const t = String(text || '').trim();
  if (!t) {
    return Promise.resolve(new Float32Array(0));
  }
  return runJob({ type: 'embed', text: t });
}

/**
 * @param {string[]} texts
 * @returns {Promise<Float32Array[]>}
 */
export function embedTextsInBrowser(texts) {
  const list = Array.isArray(texts) ? texts.map((s) => String(s || '')) : [];
  if (!list.length) return Promise.resolve([]);
  return runJob({ type: 'embedBatch', texts: list });
}

export function cosineSimilarity(a, b) {
  if (!a || !b || a.length !== b.length || a.length === 0) return 0;
  let s = 0;
  for (let i = 0; i < a.length; i += 1) s += a[i] * b[i];
  return s;
}
